import React, { useRef, useState, useEffect } from "react";
import closeIcon from "../../assets/close_icon_blue.svg";
import newChatIcon from "../../assets/new_chat_icon2.svg";
import chatIcon from "../../assets/chat_icon_blue.svg";
import { useDispatch } from "react-redux";
import { closeSearchModal } from "../../redux/slices/searchModalSlice";
import { ChatSession } from "../../types/type";
import { Link } from "react-router";
import { BeatLoader } from "react-spinners";
import { useSearchChatContent } from "./api";

interface SearchResultMessage {
  message_id: number;
  content: string;
  message_role: string;
  createdAt: string;
}

interface SearchResult {
  session_id: number;
  title: string;
  messages: SearchResultMessage[];
}

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export default function SearchModal({
  chatSessions,
}: {
  chatSessions: ChatSession[];
}) {
  const dispatch = useDispatch();
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data: searchResults = [], isLoading } = useSearchChatContent(
    debouncedSearchTerm
  ) as {
    data: SearchResult[];
    isLoading: boolean;
  };

  const extractReadableText = (content: unknown): string => {
    let current = content;

    // Keep trying to parse as long as we have a string
    for (let i = 0; i < 5; i++) {
      // Loop max 5 times to prevent infinite loops
      if (typeof current !== "string") {
        break; // It's an object, we can stop parsing
      }

      const str = current.trim();
      if (
        (str.startsWith("{") && str.endsWith("}")) ||
        (str.startsWith('"') && str.endsWith('"'))
      ) {
        try {
          // Unescape quotes and parse
          current = JSON.parse(str.replace(/\\"/g, '"'));
        } catch {
          // If parsing fails, it's probably just a regular string
          return str;
        }
      } else {
        // Doesn't look like JSON, so it's the final text
        return str;
      }
    }

    // After parsing, we should have an object. Let's extract the text.
    if (current && typeof current === "object") {
      const obj = current as any;
      if (typeof obj.summary === "string") return obj.summary;
      if (typeof obj.text === "string") return obj.text;
      if (Array.isArray(obj.details) && obj.details.length > 0) {
        return obj.details.map((d: any) => d.text).join(" ");
      }
    }

    // If all else fails, return the string representation
    return String(content);
  };

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  return (
    <div
      onClick={(e) => {
        if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
          dispatch(closeSearchModal());
        }
      }}
      className='fixed top-0 left-0 w-full h-screen z-10 flex items-center justify-center bg-black bg-opacity-10 text-black'
    >
      <div
        ref={modalRef}
        className='flex flex-col w-3/4 h-3/4 rounded-lg bg-white shadow-lg px-8 py-4'
      >
        <div className='-mx-8 px-8 flex items-center border-b-2 border-[#E0E0E0] pb-2'>
          <div className='relative flex-1'>
            <input
              ref={inputRef}
              type='text'
              className='w-full border-none outline-none py-2'
              placeholder='Search in all messages...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isLoading && (
              <BeatLoader
                size={8}
                color='#0F4988'
                className='absolute right-2 top-1/2 transform -translate-y-1/2'
              />
            )}
          </div>
          <img
            src={closeIcon}
            onClick={() => dispatch(closeSearchModal())}
            alt='Close'
            className='w-7 h-7 cursor-pointer ml-3'
          />
        </div>

        <div className='mt-4 flex flex-col overflow-y-auto'>
          {!searchTerm && (
            <Link onClick={() => dispatch(closeSearchModal())} to='/chatbot'>
              <div className='-mx-6 px-6 flex items-center justify-start mb-6 h-12 hover:bg-[#F5F5F5] cursor-pointer rounded-md'>
                <img
                  src={newChatIcon}
                  alt='New Chat'
                  className='w-6 h-6 mr-5'
                />
                <h2>New Chat</h2>
              </div>
            </Link>
          )}

          {!searchTerm &&
            chatSessions.map((session) => (
              <Link
                key={session.session_id}
                to={`/chatbot/${session.session_id}`}
                onClick={() => dispatch(closeSearchModal())}
              >
                <div className='-mx-6 px-6 flex items-center justify-start h-12 hover:bg-[#F5F5F5] cursor-pointer rounded-md'>
                  <img
                    src={chatIcon}
                    alt='Chat Icon'
                    className='w-6 h-6 mr-5'
                  />
                  <h2>{session.title}</h2>
                </div>
              </Link>
            ))}

          {searchTerm && searchResults.length === 0 && !isLoading && (
            <div className='text-center text-gray-500 py-6'>
              No messages found matching "{searchTerm}"
            </div>
          )}

          {searchTerm &&
            searchResults.map((result: SearchResult) => (
              <div key={result.session_id} className='mb-6'>
                <div className='font-medium text-gray-700 mb-2'>
                  {result.title}
                </div>

                {result.messages.map((message: SearchResultMessage) => (
                  <Link
                    key={message.message_id}
                    to={`/chatbot/${result.session_id}?highlight=${message.message_id}`}
                    onClick={() => dispatch(closeSearchModal())}
                  >
                    <div className='-mx-6 px-6 py-2 hover:bg-[#F5F5F5] cursor-pointer rounded-md flex items-start'>
                      <div
                        className={`w-2 h-2 rounded-full mt-2 mr-3 ${
                          message.message_role === "USER"
                            ? "bg-blue-500"
                            : "bg-gray-500"
                        }`}
                      ></div>
                      <div className='flex-1 overflow-hidden'>
                        <p className='text-sm text-gray-700 truncate'>
                          {extractReadableText(message.content)}
                        </p>
                        <p className='text-xs text-gray-500'>
                          {new Date(message.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
